import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  DatePicker,
  FormControlLabel,
  InputLabel,
  OnDesktop,
  OnMobile,
  Tab,
  Tabs,
} from '@hopskipdrive/react-components';
import {
  analyticsClickViewCanceledRides,
  analyticsPage,
  analyticsViewAllCanceledChecked,
} from '@rideiq/api/services/Analytics';
import { cancelAllRides, cancelAllRidesV2 } from '@rideiq/api/services/Riders';
import { RIDE_STATES, RIDE_STATES_QUERY } from '@rideiq/constants';
import { actions, Can, subjects, useAbility } from '@rideiq/contexts/AbilityContext';
import { useDebouncedDateSearch } from '@rideiq/hooks/useDebouncedDateSearch';
import { ButtonLink } from '@rideiq/shared/components';
import { PageHeading } from '@rideiq/shared/components/PageHeading';
import { useNavigation } from '@rideiq/shared/hooks/useNavigation';
import { includes } from 'lodash';

import { CancelAllRidesButtonV2 } from '../CancelAllRidesButtonV2';

import { RidesTable } from './RidesTable';
import { ButtonsContainer, Container, DateFilterContainer, FilterContainer } from './styles';
import { Params } from './types';
import { useRequestedRides } from './useRequestedRides';
import { useRidesForRider } from './useRidesForRider';

export const RidesList = ({
  riderId,
  riderName,
  riderDetailsHeadingHeight,
  riderState,
  isSharedBillingRider,
}: {
  riderId: string;
  riderName: string;
  riderDetailsHeadingHeight: number;
  riderState: string;
  isSharedBillingRider: boolean;
}) => {
  const { rides, loading, fetchRides, getDateForState } = useRidesForRider(riderId, isSharedBillingRider);
  const abilities = useAbility();
  const { navigateTo } = useNavigation();
  const [fromDate, setFromDate] = useState<Nullable<Date>>(null);
  const [params, setParams] = useState<Params>({
    state: RIDE_STATES_QUERY.upcoming,
    fromDate: null,
    sortDir: 'asc',
  });
  const { hasRideRequests } = useRequestedRides(riderId);
  const headingHeight = useRef<HTMLDivElement>(null);
  const showCanceled = includes([RIDE_STATES_QUERY.allCanceled, RIDE_STATES_QUERY.billableCanceled], params.state);
  const canClearDate = !showCanceled;
  const dateLabel = showCanceled ? 'Date' : 'Jump to';
  const currentTab = showCanceled ? RIDE_STATES_QUERY.billableCanceled : params.state;
  const isRiderActive = riderState === 'active';
  const tabLabel = {
    [RIDE_STATES_QUERY.upcoming]: 'In Progress & Upcoming',
    [RIDE_STATES_QUERY.completed]: 'Completed',
    [RIDE_STATES_QUERY.billableCanceled]: 'Canceled',
    [RIDE_STATES_QUERY.requested]: 'Requested',
  };
  const hasUpcomingRides = rides.some((ride) => RIDE_STATES.all.includes(ride.state));

  useEffect(() => {
    analyticsPage('Rider Details', { riderSlug: riderId, tab: tabLabel[params.state] });
  }, [params.state]);

  const { debounceDateSearch, cancelDateSearch } = useDebouncedDateSearch(handleDateFilter);

  const handleTabClick = (nextTab) => {
    if (nextTab === params.state) {
      return;
    }
    cancelDateSearch();
    const setDefaultDateToToday = nextTab === RIDE_STATES_QUERY.billableCanceled;
    const setDefaultDatetoNull = params.state === RIDE_STATES_QUERY.billableCanceled;

    if (nextTab === RIDE_STATES_QUERY.billableCanceled) {
      analyticsClickViewCanceledRides();
      setParams({
        ...params,
        ...(setDefaultDateToToday && { fromDate: getDateForState(new Date(), nextTab) }),
        state: RIDE_STATES_QUERY.billableCanceled,
        sortDir: 'desc',
      });
      setFromDate(new Date());
    } else if (currentTab === RIDE_STATES_QUERY.billableCanceled) {
      setParams({
        ...params,
        ...(setDefaultDatetoNull && { fromDate: null }),
        state: nextTab,
        sortDir: includes([RIDE_STATES_QUERY.upcoming, RIDE_STATES_QUERY.requested], nextTab) ? 'asc' : 'desc',
      });
      setFromDate(null);
    } else if (currentTab === RIDE_STATES_QUERY.requested) {
      setParams({
        ...params,
        fromDate: getDateForState(new Date(), nextTab),
        state: nextTab,
        sortDir: includes([RIDE_STATES_QUERY.upcoming, RIDE_STATES_QUERY.requested], nextTab) ? 'asc' : 'desc',
      });
      setFromDate(null);
    } else {
      setParams({
        ...params,
        state: nextTab,
        sortDir: includes([RIDE_STATES_QUERY.upcoming, RIDE_STATES_QUERY.requested], nextTab) ? 'asc' : 'desc',
      });
    }
  };

  function handleDateFilter(date: Date | null) {
    if (!date && !params.fromDate) {
      return;
    }
    setFromDate(date);
    setParams({ ...params, fromDate: date ? getDateForState(date, params.state) : null });
  }

  const handleOnlyBilledCancelCheckbox = (e) => {
    e.target.checked
      ? setParams({
          ...params,
          state: RIDE_STATES_QUERY.billableCanceled,
          sortDir: 'desc',
        })
      : setParams({
          ...params,
          state: RIDE_STATES_QUERY.allCanceled,
          sortDir: 'desc',
        });
    analyticsViewAllCanceledChecked(e.target.checked);
  };

  const handleCancelAllRides = async () => {
    const response = await cancelAllRides(riderId);

    if (response.type === 'success') {
      setParams({
        ...params,
      });
    }

    return response;
  };

  const handleCancelAllRidesV2 = async (payload) => {
    const response = await cancelAllRidesV2(riderId, payload);

    if (response.type === 'success') {
      setParams({
        ...params,
      });
    }

    return response;
  };

  const topContainersHeight = (headingHeight.current?.clientHeight || 0) + riderDetailsHeadingHeight + 100;

  return (
    <Container>
      <div ref={headingHeight}>
        <OnDesktop>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb="1rem">
            <PageHeading variant="h1">Rides</PageHeading>
            <ButtonsContainer>
              <Can I={actions.add} a={subjects.ride}>
                <ButtonLink
                  variant="contained"
                  color="secondary"
                  size="large"
                  state={{
                    riderName: riderName,
                    riderSlug: riderId,
                    from: window.location.pathname,
                  }}
                  to="/rides/new"
                  disabled={!isRiderActive}
                >
                  Create Ride
                </ButtonLink>
                <ButtonLink
                  color="primary"
                  size="large"
                  state={{
                    riderName: riderName,
                    riderSlug: riderId,
                    from: window.location.pathname,
                  }}
                  to="/rideseries/new"
                  disabled={!isRiderActive}
                >
                  Create Ride Series
                </ButtonLink>
              </Can>
              <Can I={actions.delete} a={subjects.ride}>
                <CancelAllRidesButtonV2
                  riderSlug={riderId}
                  riderState={riderState}
                  cancelAllRidesAction={handleCancelAllRidesV2}
                  hasRidesToCancel={hasUpcomingRides}
                />
              </Can>
            </ButtonsContainer>
          </Box>
        </OnDesktop>
        <OnMobile>
          <PageHeading variant="h1">Rides</PageHeading>

          <ButtonsContainer>
            <Can I={actions.add} a={subjects.ride}>
              <ButtonLink
                variant="contained"
                color="secondary"
                size="medium"
                state={{
                  riderName: riderName,
                  riderSlug: riderId,
                  from: window.location.pathname,
                }}
                to="/rides/new"
              >
                Create Ride
              </ButtonLink>
              <ButtonLink
                color="primary"
                size="medium"
                state={{
                  riderName: riderName,
                  riderSlug: riderId,
                  from: window.location.pathname,
                }}
                to="/rideseries/new"
              >
                Create Ride Series
              </ButtonLink>
            </Can>
            <Can I={actions.delete} a={subjects.ride}>
              <CancelAllRidesButtonV2
                riderSlug={riderId}
                riderState={riderState}
                cancelAllRidesAction={handleCancelAllRides}
                hasRidesToCancel={hasUpcomingRides}
              />
            </Can>
          </ButtonsContainer>
        </OnMobile>
        <FilterContainer>
          <Tabs
            value={currentTab}
            showIndicator
            aria-label="Rider ride views"
            data-testid="tabs-nav"
            variant="scrollable"
          >
            <Tab
              label={tabLabel.all}
              value={RIDE_STATES_QUERY.upcoming}
              onClick={() => handleTabClick(RIDE_STATES_QUERY.upcoming)}
            />
            <Tab
              label={tabLabel.completed}
              value={RIDE_STATES_QUERY.completed}
              onClick={() => handleTabClick(RIDE_STATES_QUERY.completed)}
            />
            <Tab
              label={'Canceled'}
              value={RIDE_STATES_QUERY.billableCanceled}
              onClick={() => handleTabClick(RIDE_STATES_QUERY.billableCanceled)}
            />
            {hasRideRequests && (
              <Tab
                label={tabLabel.requested}
                value={RIDE_STATES_QUERY.requested}
                onClick={() => handleTabClick(RIDE_STATES_QUERY.requested)}
              />
            )}
          </Tabs>
          <DateFilterContainer>
            <Box>
              <InputLabel style={{ minWidth: dateLabel === 'Jump to' ? '52px' : 0 }}>{dateLabel}</InputLabel>
              <DatePicker
                aria-label="jump to date"
                value={fromDate}
                onChange={(date, context) => {
                  setFromDate(date);
                  debounceDateSearch(date, !context.validationError && (!!date || canClearDate));
                }}
                slotProps={{
                  field: {
                    clearable: canClearDate,
                  },
                  textField: {
                    inputProps: {
                      'data-testid': 'rides-date-picker',
                    },
                  },
                }}
              />
            </Box>
            {currentTab === RIDE_STATES_QUERY.billableCanceled && (
              <FormControlLabel
                value={RIDE_STATES_QUERY.billableCanceled}
                control={
                  <Checkbox
                    color="primary"
                    onClick={handleOnlyBilledCancelCheckbox}
                    checked={params.state === RIDE_STATES_QUERY.billableCanceled}
                  />
                }
                label="Show only billed cancels"
                labelPlacement="end"
              />
            )}
          </DateFilterContainer>
        </FilterContainer>
      </div>
      <RidesTable
        rides={!loading ? rides : []}
        loading={loading}
        canDeleteRide={abilities.can(actions.delete, subjects.ride)}
        canEditRide={abilities.can(actions.edit, subjects.ride)}
        getMoreRides={fetchRides}
        params={params}
        onRowClick={({ slug }) => navigateTo(`/rides/${slug}`, { riderName: riderName })}
        riderId={riderId}
        riderName={riderName}
        headingHeight={topContainersHeight}
      />
    </Container>
  );
};
